import React from "react";

const ZF_FILL = "#3f3f46";
// Grundriss (Draufsicht) – Außenkante, Pfosten, Felder, Treppen
export function BalkonGrundriss({ layout, plan, selected, onSelect }) {
  const pts = layout.outline;
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  const slab = 400;
  const minX = Math.min(...xs) - 900, maxX = Math.max(...xs) + 900, minY = Math.min(...ys) - 900, maxY = Math.max(...ys) + 900;
  const W = maxX - minX, H = maxY - minY;
  const vw = 900, sc = Math.min(vw / W, 420 / H);
  const X = (x) => (x - minX) * sc, Y = (y) => (y - minY) * sc;
  const P = layout.prof.along;
  return (
    <svg viewBox={`0 0 ${W * sc} ${H * sc}`} className="w-full max-h-[440px] bg-white border border-zinc-200" data-testid="balkon-grundriss">
      {layout.geom.map((g, i) => {
        const L = g.L; const a = g.a, b = [a[0] + g.d[0] * L, a[1] + g.d[1] * L];
        const c = [b[0] + g.nrm[0] * slab, b[1] + g.nrm[1] * slab], d = [a[0] + g.nrm[0] * slab, a[1] + g.nrm[1] * slab];
        const mid = [(a[0] + b[0]) / 2 - g.nrm[0] * 350, (a[1] + b[1]) / 2 - g.nrm[1] * 350];
        return (
          <g key={i}>
            <polygon points={[a, b, c, d].map((p) => `${X(p[0])},${Y(p[1])}`).join(" ")} fill="#d4d4d8" stroke="#71717a" strokeWidth={0.6} />
            <text x={X(mid[0])} y={Y(mid[1])} fontSize={9} textAnchor="middle" fill="#18181b" fontWeight={600}>{`${plan.sides[i].label} · ${L} mm`}</text>
          </g>
        );
      })}
      {layout.stairs.map((s) => {
        const g = layout.geom[s.side];
        return Array.from({ length: s.steps }).map((_, k) => {
          const s0 = s.from, o = -(k + 1) * s.tread;
          const p1 = [g.a[0] + g.d[0] * s0 + g.nrm[0] * o, g.a[1] + g.d[1] * s0 + g.nrm[1] * o];
          const p2 = [p1[0] + g.d[0] * s.width, p1[1] + g.d[1] * s.width];
          const p3 = [p2[0] + g.nrm[0] * s.tread, p2[1] + g.nrm[1] * s.tread], p4 = [p1[0] + g.nrm[0] * s.tread, p1[1] + g.nrm[1] * s.tread];
          return <polygon key={`${s.no}-${k}`} points={[p1, p2, p3, p4].map((p) => `${X(p[0])},${Y(p[1])}`).join(" ")} fill="#e4e4e7" stroke="#71717a" strokeWidth={0.5} />;
        });
      })}
      {layout.fields.map((f) => {
        const g = layout.geom[f.side], t = (plan.sides[f.side].inset ?? plan.post.inset) + layout.prof.across / 2;
        const p1 = [g.a[0] + g.d[0] * f.from + g.nrm[0] * t, g.a[1] + g.d[1] * f.from + g.nrm[1] * t];
        const p2 = [g.a[0] + g.d[0] * f.to + g.nrm[0] * t, g.a[1] + g.d[1] * f.to + g.nrm[1] * t];
        const m = [(p1[0] + p2[0]) / 2 + g.nrm[0] * 250, (p1[1] + p2[1]) / 2 + g.nrm[1] * 250];
        const sel = selected === `f${f.no}`;
        return (
          <g key={f.no} onClick={() => onSelect?.(`f${f.no}`)} className="cursor-pointer">
            <line x1={X(p1[0])} y1={Y(p1[1])} x2={X(p2[0])} y2={Y(p2[1])} stroke={sel ? "#2563eb" : ZF_FILL} strokeWidth={sel ? 3 : 2} />
            <text x={X(m[0])} y={Y(m[1])} fontSize={7.5} textAnchor="middle" fill={sel ? "#2563eb" : "#3f3f46"}>{`ZF ${f.no} · ${f.width}`}</text>
          </g>
        );
      })}
      {layout.posts.map((p) => {
        const g = layout.geom[p.side];
        const c = [g.a[0] + g.d[0] * (p.s1 + P / 2) + g.nrm[0] * (p.t + layout.prof.across / 2), g.a[1] + g.d[1] * (p.s1 + P / 2) + g.nrm[1] * (p.t + layout.prof.across / 2)];
        const d = Math.max((p.pw || P) * sc, 4);
        return <rect key={p.id} x={X(c[0]) - d / 2} y={Y(c[1]) - d / 2} width={d} height={d} fill={p.kind === "profil45" ? "#0891b2" : p.type === "corner" ? "#18181b" : p.type === "mid" ? "#52525b" : "#2563eb"} data-testid={`balkon-post-${p.id}`} />;
      })}
    </svg>
  );
}

// Ansicht einer Seite: Pfosten, Träger, Stäbe
export function BalkonAnsicht({ layout, plan, side }) {
  const fields = layout.fields.filter((f) => f.side === side), posts = layout.posts.filter((p) => p.side === side || p.cornerSides?.includes(side));
  if (!fields.length) return null;
  const g = layout.geom[side], L = g.L, P = layout.prof.along;
  const ph = plan.post.height, H = plan.elementHeight, b = plan.bottom;
  const sc = Math.min(900 / (L + 400), 260 / (ph + 300));
  const X = (s) => (s + 200) * sc, Y = (h) => (ph + 200 - h) * sc;
  const posS = (p) => (p.side === side ? p.s1 : p.cornerSides?.[0] === side ? L - (plan.sides[side + 1]?.inset ?? plan.post.inset) - (p.pw || P) : (plan.sides[side - 1]?.inset ?? plan.post.inset));
  const m = layout.model;
  return (
    <svg viewBox={`0 0 ${(L + 400) * sc} ${(ph + 300) * sc}`} className="w-full max-h-[240px] bg-white border border-zinc-200" data-testid={`balkon-ansicht-${side}`}>
      <rect x={0} y={Y(0)} width={(L + 400) * sc} height={100 * sc} fill="#d4d4d8" />
      {fields.map((f) => {
        const x0 = f.from + plan.clearance, w = f.width;
        if (m.type === "horizontal") {
          const ew = m.einfassW;
          const eclipse = m.variant === "eclipse";
          const pitch = eclipse ? (f.stabs > 1 ? (H - m.lamH) / (f.stabs - 1) : 0) : (m.lamH + plan.gap);
          const lamX = eclipse ? x0 : x0 + ew, lamW = eclipse ? w : w - 2 * ew;
          return (
            <g key={f.no}>
              {Array.from({ length: f.stabs }).map((_, k) => <rect key={k} x={X(lamX)} y={Y(b + H - k * pitch)} width={lamW * sc} height={m.lamH * sc} fill="#52525b" />)}
              {eclipse && Array.from({ length: f.stabs }).map((_, k) => <line key={`s${k}`} x1={X(lamX)} y1={Y(b + H - m.lamH - k * pitch)} x2={X(lamX + lamW)} y2={Y(b + H - m.lamH - k * pitch)} stroke="#27272a" strokeWidth={0.6} />)}
              <rect x={X(x0)} y={Y(b + H)} width={ew * sc} height={H * sc} fill={ZF_FILL} />
              <rect x={X(x0 + w - ew)} y={Y(b + H)} width={ew * sc} height={H * sc} fill={ZF_FILL} />
              <text x={X(x0 + w / 2)} y={Y(b + H) - 4} fontSize={8} textAnchor="middle" fontWeight={600}>{`ZF ${f.no} · ${w} mm · ${f.stabs} Lamellen`}</text>
            </g>
          );
        }
        const st = Array.from({ length: f.stabs }).map((_, k) => x0 + f.edge + k * (m.stabW + plan.gap));
        const galactic = m.variant === "galactic", oh = m.overhang || 0;
        if (galactic) {
          return (
            <g key={f.no}>
              <rect x={X(x0)} y={Y(b + H - oh)} width={w * sc} height={45 * sc} fill={ZF_FILL} />
              <rect x={X(x0)} y={Y(b + 45)} width={w * sc} height={45 * sc} fill={ZF_FILL} />
              {st.map((s, k) => <rect key={k} x={X(s)} y={Y(b + H)} width={m.stabW * sc} height={H * sc} fill="#52525b" />)}
              <text x={X(x0 + w / 2)} y={Y(b + H) - 4} fontSize={8} textAnchor="middle" fontWeight={600}>{`ZF ${f.no} · ${w} mm · ${f.stabs} Latten · Überstand ${oh}`}</text>
            </g>
          );
        }
        return (
          <g key={f.no}>
            <rect x={X(x0)} y={Y(b + H)} width={w * sc} height={45 * sc} fill={ZF_FILL} />
            <rect x={X(x0)} y={Y(b + 45)} width={w * sc} height={45 * sc} fill={ZF_FILL} />
            {st.map((s, k) => <rect key={k} x={X(s)} y={Y(b + H - 45)} width={m.stabW * sc} height={(H - 90) * sc} fill="#52525b" />)}
            <text x={X(x0 + w / 2)} y={Y(b + H) - 4} fontSize={8} textAnchor="middle" fontWeight={600}>{`ZF ${f.no} · ${w} mm · ${f.stabs} Stäbe · Rand ${f.edge}`}</text>
          </g>
        );
      })}
      {posts.map((p) => <rect key={p.id} x={X(posS(p))} y={Y(ph)} width={(p.pw || P) * sc} height={ph * sc} fill={p.kind === "profil45" ? "#0891b2" : p.type === "corner" ? "#18181b" : "#3f3f46"} />)}
      {layout.hr && <rect x={X(0)} y={Y(b + H + plan.handrail.gap + layout.hr.h)} width={L * sc} height={layout.hr.h * sc} fill="#71717a" rx={layout.hr.h * sc / 2} />}
      <text x={X(-150)} y={Y(ph / 2)} fontSize={7} textAnchor="middle" transform={`rotate(-90 ${X(-150)} ${Y(ph / 2)})`}>{`Pfosten ${ph} · Element ${H} · Bodenluft ${b}`}</text>
    </svg>
  );
}
